// src/components/dashboard/tabs/SummaryTab.jsx
// サマリータブ 強化版 v2
import ShareCard from '../../ShareCard';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AICoaching from './AICoaching';
import { useAuth } from '../../../AuthContext';
import { app } from '../../../firebase';
import { getFirestore, collection, query, orderBy, limit, getDocs, where, onSnapshot } from 'firebase/firestore';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
  PieChart, Pie, Cell, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  Clock, TrendingUp, Target, Zap, ChevronRight,
  BookOpen, Star, Calendar, Award, Flame, BarChart2,
  ArrowRight, CheckCircle, Circle, Lightbulb
} from 'lucide-react';
import { diagnosePlateauArchetype } from '../../../services/aiAnalysisService.js';
import { IconSeedling, IconBook, IconBolt, IconFlame, IconRocket, IconTrophy, IconMic, IconChart, IconBrain, IconTarget, IconTrendUp, IconUsers, IconKey, IconLightbulb, IconMedal, IconCalendar, IconActivity } from '../../common/Icons.jsx';
import { plateauArchetypes } from '../../../data/plateauArchetypes.js';
import { BADGES, computeBadgeStats } from '../../../data/badges.js';

const db = getFirestore(app);

/* ═══════════════════════════════════════
   定数・ユーティリティ
═══════════════════════════════════════ */
const MILESTONE_ICONS = {
  '🌱': (c) => <IconSeedling size={16} color={c}/>,
  '📚': (c) => <IconBook size={16} color={c}/>,
  '⚡': (c) => <IconBolt size={16} color={c}/>,
  '🔥': (c) => <IconFlame size={16} color={c}/>,
  '🚀': (c) => <IconRocket size={16} color={c}/>,
  '🏆': (c) => <IconTrophy size={16} color={c}/>,
};
const MILESTONES = [
  { hours: 20,   name: '最初の一歩',     emoji: '🌱' },
  { hours: 100,  name: '初心者卒業',     emoji: '📚' },
  { hours: 300,  name: '第一フェーズ',   emoji: '⚡' },
  { hours: 500,  name: '中級者',         emoji: '🔥' },
  { hours: 750,  name: '上級者手前',     emoji: '🚀' },
  { hours: 1000, name: 'スペシャリスト到達', emoji: '🏆' },
];

const GROWTH_TYPES = {
  sprinter:    { label: 'スプリンター型',   emoji: '⚡', Icon: IconBolt,    color: '#f59e0b', desc: '短期集中で一気に習得するタイプ。集中力が高く、ブーストがかかると急激に成長する。' },
  climber:     { label: 'クライマー型',     emoji: '🧗', Icon: IconTrendUp, color: '#3b82f6', desc: '着実に段階を踏んで成長するタイプ。計画的で安定した積み上げが得意。' },
  phoenix:     { label: 'フェニックス型',   emoji: '🔥', Icon: IconFlame,   color: '#ef4444', desc: '一度停滞しても劇的に復活するタイプ。挫折から学ぶ力が強い。' },
  breakthrough: { label: 'ブレイクスルー型', emoji: '💥', Icon: IconRocket,  color: '#8b5cf6', desc: '長期間の準備後に突然大きく飛躍するタイプ。熟成期間が成長の鍵。' },
  marathoner:  { label: 'マラソナー型',     emoji: '🏃', Icon: IconMedal,   color: '#10b981', desc: '長期にわたって安定して成長し続けるタイプ。継続力と持久力が最大の武器。' },
};

const ABILITY_COLORS = {
  '粘り強さ':     '#3b82f6',
  '思考力':       '#10b981',
  'バランス力':   '#8b5cf6',
  'イノベーション力': '#ef4444',
  '共感力':       '#f59e0b',
  '協働力':       '#6366f1',
  '主体性':       '#ec4899',
  '振り返り力':   '#f97316',
  '最適化力':     '#14b8a6',
};

const getWeekStart = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(now.setDate(diff));
};

const getMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

/* ═══════════════════════════════════════
   カテゴリ定義
═══════════════════════════════════════ */
const CATEGORIES = [
  { id: 'reading',  label: '読書',   emoji: '📚', color: '#3b82f6' },
  { id: 'practice', label: '実践',   emoji: '⚡', color: '#f59e0b' },
  { id: 'research', label: '調査',   emoji: '🔍', color: '#8b5cf6' },
  { id: 'meeting',  label: '議論',   emoji: '💬', color: '#10b981' },
  { id: 'creation', label: '制作',   emoji: '🎨', color: '#ef4444' },
  { id: 'review',   label: '振り返り', emoji: '🔄', color: '#6366f1' },
  { id: 'other',    label: 'その他', emoji: '📝', color: '#9ca3af' },
];

/* ═══════════════════════════════════════
   カスタムフック: 学習ログ取得
═══════════════════════════════════════ */
const useLearningStats = (uid) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) { setLoading(false); return; }
    const fetchLogs = async () => {
      try {
        // users/{uid}/logs サブコレクションを試みる
        const logsRef = collection(db, 'users', uid, 'logs');
        const q = query(logsRef, orderBy('date', 'desc'), limit(60));
        const snap = await getDocs(q);
        if (!snap.empty) {
          setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } else {
          // フォールバック: learning_logs コレクション
          const altRef = collection(db, 'learning_logs');
          const altQ = query(altRef, where('userId', '==', uid), orderBy('date', 'desc'), limit(60));
          const altSnap = await getDocs(altQ);
          setLogs(altSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      } catch (e) {
        // エラー時は空配列のまま
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [uid]);

  // 統計計算
  const stats = useMemo(() => {
    const weekStart  = getWeekStart();
    const monthStart = getMonthStart();

    let weeklyHours  = 0;
    let monthlyHours = 0;
    let totalHours   = 0;

    logs.forEach(log => {
      const h = Number(log.hours || log.duration || 0);
      const d = log.date?.toDate ? log.date.toDate() : new Date(log.date);
      totalHours += h;
      if (d >= monthStart) monthlyHours += h;
      if (d >= weekStart)  weeklyHours += h;
    });

    // 過去30日のチャートデータ
    const chartMap = {};
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = `${d.getMonth() + 1}/${d.getDate()}`;
      chartMap[key] = { date: key, hours: 0, cumulative: 0 };
    }
    let cum = 0;
    [...logs].reverse().forEach(log => {
      const d = log.date?.toDate ? log.date.toDate() : new Date(log.date);
      const key = `${d.getMonth() + 1}/${d.getDate()}`;
      if (chartMap[key]) chartMap[key].hours += Number(log.hours || 0);
    });
    Object.keys(chartMap).forEach(k => {
      cum += chartMap[k].hours;
      chartMap[k].cumulative = Math.round(cum * 10) / 10;
    });

    const recentLogs = logs.slice(0, 5);

    return { weeklyHours, monthlyHours, totalHours, chartData: Object.values(chartMap), recentLogs };
  }, [logs]);

  // 連続学習日数計算
  const streak = useMemo(() => {
    if (!logs.length) return 0;
    const today = new Date(); today.setHours(0,0,0,0);
    const logDays = new Set(logs.map(l => {
      const d = l.date?.toDate ? l.date.toDate() : new Date(l.date?.seconds * 1000 || 0);
      d.setHours(0,0,0,0); return d.getTime();
    }));
    let count = 0;
    let check = new Date(today);
    // 今日または昨日から遡って連続チェック
    if (!logDays.has(check.getTime())) {
      check.setDate(check.getDate() - 1);
      if (!logDays.has(check.getTime())) return 0;
    }
    while (logDays.has(check.getTime())) {
      count++;
      check.setDate(check.getDate() - 1);
    }
    return count;
  }, [logs]);

  return { stats, loading, streak };
};

/* ═══════════════════════════════════════
   UIパーツ
═══════════════════════════════════════ */

// ─── 統計カード ───
const StatCard = ({ icon: Icon, label, value, sub, color = '#3b6fd4', highlight = false }) => (
  <div style={{
    background: highlight ? `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)` : '#fff',
    border: `1.5px solid ${highlight ? color + '40' : '#e5e7eb'}`,
    borderRadius: 14, padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: 6,
    transition: 'box-shadow 0.2s',
  }}
  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ background: color + '18', borderRadius: 8, padding: 6, display: 'flex' }}>
        <Icon size={14} color={color} />
      </div>
      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span style={{ fontSize: 26, fontWeight: 800, color: highlight ? color : '#111827', lineHeight: 1 }}>
        {typeof value === 'number' ? value.toFixed(1) : value}
      </span>
      <span style={{ fontSize: 13, color: '#6b7280' }}>時間</span>
    </div>
    {sub && <span style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</span>}
  </div>
);

// ─── セクションヘッダー ───
const SectionHeader = ({ icon: Icon, title, color = '#111827', action, onAction }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon size={16} color={color} />
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>
        {title}
      </h3>
    </div>
    {action && (
      <button
        onClick={onAction}
        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
      >
        {action} <ChevronRight size={12} />
      </button>
    )}
  </div>
);

// ─── 1000時間プログレス ───
const ThousandHoursProgress = ({ totalHours }) => {
  const progress = Math.min((totalHours / 1000) * 100, 100);
  const nextMilestone = MILESTONES.find(m => m.hours > totalHours) || MILESTONES[MILESTONES.length - 1];
  const prevMilestone = [...MILESTONES].reverse().find(m => m.hours <= totalHours);
  const remaining = Math.max(nextMilestone.hours - totalHours, 0);

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
      <SectionHeader icon={Award} title="成長の軌跡" color="#f59e0b" />

      {/* プログレスバー */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <div style={{ height: 10, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 999,
            background: 'linear-gradient(90deg, #f59e0b, #ef4444)',
            width: `${progress}%`,
            transition: 'width 0.8s ease',
            boxShadow: '0 1px 4px rgba(245,158,11,0.4)',
          }} />
        </div>
        {/* マイルストーンマーカー */}
        {MILESTONES.slice(0, -1).map(m => (
          <div key={m.hours} style={{
            position: 'absolute', top: -3,
            left: `${(m.hours / 1000) * 100}%`,
            transform: 'translateX(-50%)',
            width: 3, height: 16, borderRadius: 2,
            background: totalHours >= m.hours ? '#f59e0b' : '#d1d5db',
          }} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          {prevMilestone ? `${prevMilestone.emoji} ${prevMilestone.name}` : '出発点'}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
          {totalHours.toFixed(1)}h / 1000h
        </span>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          {nextMilestone.emoji} {nextMilestone.name}まで残り{remaining.toFixed(1)}h
        </span>
      </div>

      {/* マイルストーン一覧 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {MILESTONES.map(m => (
          <div key={m.hours} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: totalHours >= m.hours ? '#fef3c7' : '#f9fafb',
            border: `1px solid ${totalHours >= m.hours ? '#fbbf24' : '#e5e7eb'}`,
            borderRadius: 8, padding: '4px 10px',
          }}>
            <span style={{ fontSize: 12 }}>{m.emoji}</span>
            <span style={{ fontSize: 11, color: totalHours >= m.hours ? '#92400e' : '#9ca3af', fontWeight: totalHours >= m.hours ? 600 : 400 }}>
              {m.hours}h
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── スキル別内訳グラフ ───
const CategoryChart = ({ logs }) => {
  const data = React.useMemo(() => {
    const CATS = [
      { id: 'reading',  label: '読書',    emoji: '📚', color: '#3b82f6' },
      { id: 'practice', label: '実践',    emoji: '⚡', color: '#f59e0b' },
      { id: 'research', label: '調査',    emoji: '🔍', color: '#8b5cf6' },
      { id: 'meeting',  label: '議論',    emoji: '💬', color: '#10b981' },
      { id: 'creation', label: '制作',    emoji: '🎨', color: '#ef4444' },
      { id: 'review',   label: '振り返り', emoji: '🔄', color: '#6366f1' },
      { id: 'other',    label: 'その他',  emoji: '📝', color: '#9ca3af' },
    ];
    const map = {};
    logs.forEach(log => { const c = log.category || 'other'; map[c] = (map[c]||0) + Number(log.hours||0); });
    return CATS.map(c => ({ ...c, hours: map[c.id]||0 })).filter(c => c.hours > 0).sort((a,b) => b.hours - a.hours);
  }, [logs]);
  const total = data.reduce((s,d) => s + d.hours, 0);
  if (data.length === 0) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:120, color:'#9ca3af', fontSize:13, flexDirection:'column', gap:8 }}>
      <span style={{ fontSize:28, opacity:0.4 }}>📊</span>学習ログを記録するとグラフが表示されます
    </div>
  );
  const RADIAN = Math.PI / 180;
  const renderLabel = ({ cx,cy,midAngle,innerRadius,outerRadius,percent }) => {
    if (percent < 0.08) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    return <text x={cx + r*Math.cos(-midAngle*RADIAN)} y={cy + r*Math.sin(-midAngle*RADIAN)} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>{`${(percent*100).toFixed(0)}%`}</text>;
  };
  return (
    <div className="nobishiro-category-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:16, alignItems:'center' }}>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="hours" labelLine={false} label={renderLabel}>
            {data.map((entry,i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip formatter={(v) => [`${v.toFixed(1)}h`,'']} contentStyle={{ fontSize:12, borderRadius:8, border:'1px solid #e5e7eb' }} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {data.map(d => (
          <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:10, height:10, borderRadius:'50%', background:d.color, flexShrink:0 }} />
            <span style={{ fontSize:12, color:'#374151', flex:1 }}>{d.emoji} {d.label}</span>
            <span style={{ fontSize:12, fontWeight:700, color:'#111827' }}>{d.hours.toFixed(1)}h</span>
            <span style={{ fontSize:10, color:'#9ca3af', width:32, textAlign:'right' }}>{((d.hours/total)*100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── 成長グラフ ───
const GrowthChart = ({ chartData }) => {
  const hasData = chartData.some(d => d.hours > 0);

  if (!hasData) {
    return (
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
        <SectionHeader icon={TrendingUp} title="成長曲線" color="#3b6fd4" />
        <div style={{ height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', gap: 8 }}>
          <BarChart2 size={32} strokeWidth={1} />
          <p style={{ margin: 0, fontSize: 13 }}>学習ログを記録すると成長曲線が表示されます</p>
        </div>
      </div>
    );
  }

  const avgHours = chartData.filter(d => d.hours > 0).reduce((s, d) => s + d.hours, 0) /
    Math.max(chartData.filter(d => d.hours > 0).length, 1);

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
      <SectionHeader icon={TrendingUp} title="成長曲線（過去30日）" color="#3b6fd4" />
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#3b6fd4" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b6fd4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 9, fill: '#9ca3af' }}
            interval={6}
            tickLine={false}
          />
          <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
            formatter={(v) => [`${v}h`, '学習時間']}
          />
          <ReferenceLine y={avgHours} stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={1.5} />
          <Area
            type="monotone" dataKey="hours"
            stroke="#3b6fd4" strokeWidth={2}
            fill="url(#colorHours)"
            dot={false}
            activeDot={{ r: 4, fill: '#3b6fd4' }}
          />
        </AreaChart>
      </ResponsiveContainer>
      <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
        黄点線: 平均 {avgHours.toFixed(1)}h/日
      </p>
    </div>
  );
};

// ─── 今週の目標 ───
const WeeklyGoal = ({ weeklyHours, profile, onEditGoal }) => {
  const goal = profile?.weeklyGoalHours || 7; // デフォルト7時間/週
  const progress = Math.min((weeklyHours / goal) * 100, 100);
  const achieved = weeklyHours >= goal;
  const dayOfWeek = new Date().getDay();
  const daysLeft = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

  return (
    <div style={{
      background: achieved ? 'linear-gradient(135deg, #ecfdf5, #f0fdf4)' : '#fff',
      border: `1.5px solid ${achieved ? '#6ee7b7' : '#e5e7eb'}`,
      borderRadius: 14, padding: '16px 20px',
    }}>
      <SectionHeader icon={Target} title="今週の目標" color={achieved ? '#059669' : '#3b6fd4'} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {achieved ? '✅ 今週の目標達成！' : `残り${daysLeft}日・あと${Math.max(goal - weeklyHours, 0).toFixed(1)}h`}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: achieved ? '#059669' : '#111827' }}>
                {weeklyHours.toFixed(1)}h / {goal}h
              </span>
              {onEditGoal && (
                <button
                  onClick={onEditGoal}
                  style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}
                >
                  ✏️ 変更
                </button>
              )}
            </div>
          </div>
          <div style={{ height: 8, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 999,
              background: achieved
                ? 'linear-gradient(90deg, #059669, #10b981)'
                : 'linear-gradient(90deg, #3b6fd4, #6366f1)',
              width: `${progress}%`, transition: 'width 0.6s ease',
            }} />
          </div>
        </div>
      </div>
      {/* 曜日インジケーター */}
      <div style={{ display: 'flex', gap: 4 }}>
        {['月', '火', '水', '木', '金', '土', '日'].map((day, i) => {
          const dayNum = i + 1;
          const today = new Date().getDay() === 0 ? 7 : new Date().getDay();
          const isPast = dayNum < today;
          const isToday = dayNum === today;
          return (
            <div key={day} style={{
              flex: 1, textAlign: 'center',
              background: isToday ? '#3b6fd4' : isPast ? '#dbeafe' : '#f9fafb',
              borderRadius: 6, padding: '4px 0',
              color: isToday ? '#fff' : isPast ? '#3b6fd4' : '#9ca3af',
              fontSize: 10, fontWeight: isToday ? 700 : 400,
              border: `1px solid ${isToday ? '#3b6fd4' : isPast ? '#bfdbfe' : '#e5e7eb'}`,
            }}>
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── 最近のログ ───
const RecentLogs = ({ logs }) => {
  if (logs.length === 0) {
    return (
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
        <SectionHeader icon={BookOpen} title="最近の学習ログ" color="#6b7280" />
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>
          <BookOpen size={28} strokeWidth={1} style={{ display: 'block', margin: '0 auto 8px' }} />
          まだ学習ログがありません。<br />ログを記録して成長を可視化しましょう。
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
      <SectionHeader icon={BookOpen} title="最近の学習ログ" color="#3b6fd4" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {logs.map((log, i) => {
          const d = log.date?.toDate ? log.date.toDate() : new Date(log.date);
          const dateStr = d instanceof Date && !isNaN(d)
            ? `${d.getMonth() + 1}/${d.getDate()}`
            : '—';
          const hours = Number(log.hours || log.duration || 0);
          return (
            <div key={log.id || i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px',
              background: i === 0 ? '#eff6ff' : '#f9fafb',
              borderRadius: 10,
              border: `1px solid ${i === 0 ? '#bfdbfe' : '#f3f4f6'}`,
            }}>
              <div style={{
                background: i === 0 ? '#3b6fd4' : '#e5e7eb',
                color: i === 0 ? '#fff' : '#6b7280',
                borderRadius: 8, padding: '5px 8px',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>
                {dateStr}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {log.category && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>{log.category}</div>
                )}
                {log.notes && (
                  <div style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.notes}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                <Clock size={12} color="#6b7280" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{hours.toFixed(1)}h</span>
              </div>
              {log.quality_rating && (
                <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={10} fill={s <= log.quality_rating ? '#f59e0b' : 'none'} color={s <= log.quality_rating ? '#f59e0b' : '#d1d5db'} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── 次のアクション ───
const NextActions = ({ profile, stats }) => {
  const actions = useMemo(() => {
    const list = [];
    const total = stats.totalHours;
    const weekly = stats.weeklyHours;

    if (total < 20) {
      list.push({ icon: null, IconComp: IconSeedling, iconColor: '#16a34a', title: '最初の20時間を目指そう', desc: `あと${Math.max(20 - total, 0).toFixed(1)}時間で「最初の一歩」マイルストーンに到達します。`, priority: 'high' });
    } else if (total < 100) {
      list.push({ icon: null, IconComp: IconBook, iconColor: '#1d4ed8', title: '100時間まで継続しよう', desc: `あと${Math.max(100 - total, 0).toFixed(1)}時間で「初心者卒業」に到達します。`, priority: 'high' });
    }

    if (weekly < 3) {
      list.push({ icon: null, IconComp: IconCalendar, iconColor: '#b45309', title: '今週の学習量を増やそう', desc: '週3時間以上の学習が成長を加速させます。今週はまだ伸びしろがあります。', priority: 'medium' });
    }

    if (!profile?.growthWallProfile) {
      list.push({ icon: null, IconComp: IconTarget, iconColor: '#7c3aed', title: '成長パターン診断を受けよう', desc: '「成長パターン」タブで診断を受けると、あなたの壁と突破口が見えてきます。', priority: 'medium' });
    }

    if (!profile?.neurodiversityProfile) {
      list.push({ icon: null, IconComp: IconBrain, iconColor: '#0891b2', title: '認知特性を把握しよう', desc: '「認知特性」タブで診断を受けると、最適な学習スタイルが分かります。', priority: 'low' });
    }

    list.push({ icon: null, IconComp: IconBolt, iconColor: '#d97706', title: 'ブレイクスルー診断を活用しよう', desc: '現在直面している課題をブレイクスルー診断に入力してみましょう。', priority: 'low' });

    return list.slice(0, 3);
  }, [profile, stats]);

  const priorityConfig = {
    high:   { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', label: '重要' },
    medium: { bg: '#fff7ed', border: '#fed7aa', color: '#ea580c', label: '推奨' },
    low:    { bg: '#eff6ff', border: '#bfdbfe', color: '#3b6fd4', label: 'ヒント' },
  };

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
      <SectionHeader icon={Lightbulb} title="おすすめの次のアクション" color="#f59e0b" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {actions.map((a, i) => {
          const pc = priorityConfig[a.priority];
          return (
            <div key={i} style={{
              background: pc.bg, border: `1px solid ${pc.border}`,
              borderRadius: 10, padding: '12px 14px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              animation: `fadeIn 0.3s ease ${i * 0.06}s both`,
            }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: (a.iconColor || '#6366f1') + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {a.IconComp ? <a.IconComp size={16} color={a.iconColor || '#6366f1'}/> : <span style={{ fontSize: 16 }}>{a.icon}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{a.title}</span>
                  <span style={{
                    background: pc.color + '20', color: pc.color,
                    fontSize: 9, fontWeight: 800, padding: '1px 6px',
                    borderRadius: 20, letterSpacing: '0.05em',
                  }}>{pc.label}</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{a.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ─── NOBISHIROコンピテンシーレーダーチャート ───
const NOBISHIRO_ABILITIES = [
  { key: 'N',  name: '粘り強さ' },
  { key: 'O',  name: '思考力' },
  { key: 'B',  name: 'バランス力' },
  { key: 'I',  name: 'イノベーション力' },
  { key: 'S',  name: '共感力' },
  { key: 'H',  name: '協働力' },
  { key: 'I2', name: '主体性' },
  { key: 'R',  name: '振り返り力' },
  { key: 'O2', name: '最適化力' },
];

const NobishiroRadarChart = ({ profile }) => {
  const scores = profile?.nobishiroScores;
  if (!scores || Object.keys(scores).length === 0) {
    return (
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>NOBISHIROコンピテンシー®</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>9つの能力レーダーチャート</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af' }}>
          <p style={{ fontSize: 13, margin: '0 0 4px' }}>NOBISHIRO診断を受けると表示されます</p>
          <p style={{ fontSize: 12, margin: 0 }}>サイドメニュー「NOBISHIRO診断」から受診できます</p>
        </div>
      </div>
    );
  }

  const radarData = NOBISHIRO_ABILITIES.map(a => ({
    ability: a.name,
    score: Math.round((scores[a.key] || 0) / 5 * 100),
    fullMark: 100,
  }));

  const avgScore = Math.round(radarData.reduce((s, d) => s + d.score, 0) / radarData.length);
  const topAbility = [...radarData].sort((a, b) => b.score - a.score)[0];
  const weakAbility = [...radarData].sort((a, b) => a.score - b.score)[0];

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>NOBISHIROコンピテンシー®</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>9つの能力レーダーチャート</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#7c3aed' }}>{avgScore}<span style={{ fontSize: 11, fontWeight: 400 }}>点</span></div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>総合スコア</div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="ability" tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#9ca3af' }} tickCount={4} />
          <Radar name="スコア" dataKey="score" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.25} strokeWidth={2} />
          <Tooltip formatter={(v) => [v + '点', 'スコア']} />
        </RadarChart>
      </ResponsiveContainer>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700, marginBottom: 3 }}>最も高い能力</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#5b21b6' }}>✦ {topAbility.ability}</div>
          <div style={{ fontSize: 12, color: '#7c3aed' }}>{topAbility.score}点</div>
        </div>
        <div style={{ background: '#fef9c3', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: '#92400e', fontWeight: 700, marginBottom: 3 }}>伸びしろのある能力</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#78350f' }}>◎ {weakAbility.ability}</div>
          <div style={{ fontSize: 12, color: '#92400e' }}>{weakAbility.score}点</div>
        </div>
      </div>
    </div>
  );
};


// ─── SNS共有ボタン ───
const SNSShareButtons = ({ totalHours, streak, archetype, growthType, profile, compact = false }) => {
  const [copied, setCopied] = React.useState(false);
  const APP_URL = 'https://www.nobishiro-competency.com';
  const archetypeNames = { LOGICAL_ARCHITECT: '論理的設計者', EMPATHETIC_CONNECTOR: '共感的連結者', BOLD_PIONEER: '大胆な開拓者', FLEXIBLE_ARTISAN: '柔軟な実践者' };
  const growthNames = { sprinter: 'スプリンター型', climber: 'クライマー型', phoenix: 'フェニックス型', breakthrough: 'ブレイクスルー型', marathoner: 'マラソナー型' };
  const arcName = archetypeNames[archetype] || '成長中';
  const grName  = growthNames[growthType] || '';
  const avgScore = profile?.nobishiroScores
    ? Math.round(Object.values(profile.nobishiroScores).reduce((s,v)=>s+v,0) / Object.keys(profile.nobishiroScores).length / 5 * 100)
    : null;
  const shareText = `【学習記録】${Math.round(totalHours||0)}時間、積み上げてきました📚\n成長タイプ：${arcName}${grName ? '×'+grName : ''}${avgScore ? '｜スコア'+avgScore+'点' : ''}\n\n努力って、続けると本当に見えてくる。\n一緒に成長記録つけてみませんか？\n→ ${APP_URL}\n\n#社会人勉強 #スキルアップ #自己成長 #学習記録 #NOBISHIRO`;
  const handleShare = (id) => {
    if (id === 'x')    window.open('https://twitter.com/intent/tweet?text='+encodeURIComponent(shareText), '_blank');
    if (id === 'line') window.open('https://social-plugins.line.me/lineit/share?url='+encodeURIComponent(APP_URL)+'&text='+encodeURIComponent(shareText), '_blank');
    if (id === 'copy') { navigator.clipboard?.writeText(shareText); setCopied(true); setTimeout(()=>setCopied(false), 2500); }
  };
  const btns = [
    { id: 'x',    label: 'X',    bg: '#000',    icon: '𝕏' },
    { id: 'line', label: 'LINE', bg: '#06C755', icon: 'L' },
    { id: 'copy', label: copied ? 'コピー済！' : 'コピー', bg: copied ? '#10b981' : '#6b7280', icon: '🔗' },
  ];
  if (compact) return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>シェア：</span>
      {btns.map(s => (
        <button key={s.id} onClick={() => handleShare(s.id)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: s.bg, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          <span>{s.icon}</span>{s.label}
        </button>
      ))}
    </div>
  );
  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
        成長記録をシェアする
      </div>
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
        {shareText}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {btns.map(s => (
          <button key={s.id} onClick={() => handleShare(s.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: s.bg, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <span style={{ fontWeight: 900 }}>{s.icon}</span>{s.label}
          </button>
        ))}
      </div>
      <p style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center', margin: '8px 0 0' }}>※ Instagramは「診断結果をシェア」ボタンから画像ダウンロード後に投稿できます</p>
    </div>
  );
};

// ─── 成長タイプ詳細 ───
const GrowthTypeDetail = ({ profile }) => {
  const growthType = profile?.growthWallProfile?.growthType
    || profile?.growthType
    || null;

  const archetypeKey = profile ? diagnosePlateauArchetype(profile) : null;
  const archetypeData = archetypeKey ? plateauArchetypes[archetypeKey] : null;
  const archetype = archetypeData?.name || null;

  const typeInfo = growthType ? GROWTH_TYPES[growthType] : null;
  const description = archetypeData?.description || profile?.growthWallProfile?.description || '';
  const recommendation = archetypeData?.advice || profile?.growthWallProfile?.recommendation || '';

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
      {/* 成長アーキタイプ */}
      <div style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
        borderRadius: 12, padding: '18px 20px', marginBottom: 14,
        border: '1px solid #bfdbfe',
        textAlign: 'center',
      }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
          <IconKey size={24} color="#1e40af"/>
        </div>
        <div style={{ fontSize: 11, color: '#3b6fd4', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 6 }}>
          あなたの成長アーキタイプ
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 8 }}>
          {archetype || '未診断'}
        </div>
        {description && (
          <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.7 }}>{description}</p>
        )}
      </div>

      {/* 成長タイプ */}
      {typeInfo && (
        <div style={{
          background: typeInfo.color + '12',
          border: `1.5px solid ${typeInfo.color}40`,
          borderRadius: 12, padding: '14px 16px', marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: typeInfo.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {typeInfo.Icon ? <typeInfo.Icon size={20} color={typeInfo.color}/> : <span style={{ fontSize: 18 }}>{typeInfo.emoji}</span>}
            </div>
            <div>
              <div style={{ fontSize: 11, color: typeInfo.color, fontWeight: 700, letterSpacing: '0.06em' }}>成長タイプ</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{typeInfo.label}</div>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>{typeInfo.desc}</p>
        </div>
      )}

      {/* 推奨アクション */}
      {recommendation && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: 10, padding: '13px 15px',
          display: 'flex', gap: 10,
        }}>
          <Lightbulb size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 11, color: '#d97706', fontWeight: 700, marginBottom: 4 }}>
              プラトー期を乗り越えるための推奨アクション
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>{recommendation}</p>
          </div>
        </div>
      )}

      {!archetype && !typeInfo && (
        <div style={{ textAlign: 'center', padding: '12px 0', color: '#9ca3af', fontSize: 13 }}>
          「成長パターン」タブで診断を受けると、あなたの成長タイプが分かります。
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   メインコンポーネント
═══════════════════════════════════════ */
export default function SummaryTab({ profile, onTabChange, updateProfile }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState(profile?.weeklyGoalHours || 7);
  const { stats: rawStats, loading: statsLoading, streak } = useLearningStats(user?.uid);
  const stats = rawStats || { weeklyHours: 0, monthlyHours: 0, totalHours: 0, chartData: [], recentLogs: [] };

  // ─── 診断データ（ShareCard用） ───
  const growthType = profile?.growthWallProfile?.growthType || profile?.growthType || null;
  const _archetypeKey  = profile ? diagnosePlateauArchetype(profile) : null;
  const _archetypeData = _archetypeKey ? plateauArchetypes[_archetypeKey] : null;
  const archetype      = _archetypeData?.name || null;

  // ─── バッジ計算 ───
  const earnedBadges = useMemo(() => {
    if (!stats || !stats.recentLogs) return [];
    const badgeStats = computeBadgeStats(stats.recentLogs, streak, profile?.weeklyGoalHours || 7);
    return BADGES.filter(b => b.condition(badgeStats));
  }, [stats.recentLogs, streak, profile?.weeklyGoalHours]);

  // ─── カレンダーデータ（過去35日） ───
  const calendarData = useMemo(() => {
    const days = [];
    const today = new Date(); today.setHours(0,0,0,0);
    const logMap = {};
    (stats.recentLogs || []).forEach(log => {
      const d = log.date?.toDate ? log.date.toDate() : new Date(log.date?.seconds * 1000 || 0);
      d.setHours(0,0,0,0);
      const key = d.getTime();
      logMap[key] = (logMap[key] || 0) + Number(log.hours || 0);
    });
    for (let i = 34; i >= 0; i--) {
      const d = new Date(today); d.setDate(today.getDate() - i);
      days.push({ date: d, hours: logMap[d.getTime()] || 0 });
    }
    return days;
  }, [stats.recentLogs]);

  // ─── プッシュ通知 ───
  const [notifEnabled, setNotifEnabled] = useState(() => {
    try { return localStorage.getItem('nobishiro_notif_enabled') === 'true'; } catch { return false; }
  });
  const [notifTime, setNotifTime] = useState(() => {
    try { return localStorage.getItem('nobishiro_notif_time') || '20:00'; } catch { return '20:00'; }
  });
  const [showNotifSettings, setShowNotifSettings] = useState(false);

  // 通知設定をlocalStorageに保存
  useEffect(() => {
    try {
      localStorage.setItem('nobishiro_notif_enabled', notifEnabled);
      localStorage.setItem('nobishiro_notif_time', notifTime);
    } catch {}
  }, [notifEnabled, notifTime]);

  // ページ読み込み時に通知許可状態を確認
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      setNotifEnabled(false);
    }
  }, []);

  const requestNotifPermission = async () => {
    if (!('Notification' in window)) { alert('このブラウザは通知をサポートしていません'); return; }
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      setNotifEnabled(true);
      new Notification('NOBISHIRO', { body: '🎉 通知が有効になりました！毎日の学習をお知らせします', icon: '/favicon.ico' });
    }
  };

  const sendTestNotif = () => {
    if (Notification.permission === 'granted') {
      new Notification('📚 学習タイムです！', { body: `今日の目標まであと${Math.max(0, (profile?.weeklyGoalHours || 7) / 7 - stats.todayHours || 0).toFixed(1)}時間。頑張りましょう！`, icon: '/favicon.ico' });
    }
  };

  // profileのtotalHoursがあればそちらを優先
  const totalHours = profile?.totalHours || stats.totalHours;
  const weeklyHours = profile?.weeklyHours || stats.weeklyHours;
  const monthlyHours = profile?.monthlyHours || stats.monthlyHours;
  const recentLogs = stats.recentLogs;
  const chartData = stats.chartData;

  const displayName = profile?.displayName || user?.displayName || '';

  return (
    <div style={{ fontFamily: "'Hiragino Sans', 'Yu Gothic', 'Noto Sans JP', sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
        @keyframes streakPop { 0% { transform: scale(0.8); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        * { -webkit-font-smoothing: antialiased; box-sizing: border-box; }
        .nobishiro-grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; margin-bottom: 20px; }
        .nobishiro-stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
        @media (max-width: 480px) {
          .nobishiro-stat-grid { grid-template-columns: 1fr !important; gap: 8px !important; }
          .nobishiro-grid-2 { grid-template-columns: 1fr !important; }
          .nobishiro-category-grid { grid-template-columns: 1fr !important; }
          .nobishiro-milestone-row { flex-wrap: wrap !important; gap: 4px !important; }
          .nobishiro-header-stats { flex-direction: column !important; gap: 8px !important; }
        }
        @media (max-width: 640px) {
          .nobishiro-stat-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .nobishiro-card-pad { padding: 14px 16px !important; }
          .nobishiro-log-btn { padding: 18px 16px !important; font-size: 14px !important; }
          .nobishiro-modal { padding: 20px 16px !important; margin: 16px !important; }
          .nobishiro-pie-chart { height: 150px !important; }
          .nobishiro-category-grid { grid-template-columns: 1fr !important; }
        }
        .nobishiro-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
        .nobishiro-streak-badge { animation: streakPop 0.5s ease forwards; }
        @media (max-width: 600px) {
          .nobishiro-stats-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .nobishiro-grid-2 { grid-template-columns: 1fr; }
          .nobishiro-header-title { font-size: 20px !important; }
          .nobishiro-header-hours { font-size: 22px !important; }
          .nobishiro-card-pad { padding: 14px 16px !important; }
          .nobishiro-log-btn { padding: 18px 16px !important; font-size: 14px !important; }
          .nobishiro-modal { padding: 20px 16px !important; margin: 16px !important; }
          .nobishiro-nav-shortcuts { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ─── ウェルカムヘッダー ─── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #312e81 100%)',
        borderRadius: 16, padding: '20px 24px', marginBottom: 20, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#93c5fd', letterSpacing: '0.08em', marginBottom: 4 }}>
            NOBISHIROコンピテンシー®
          </div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, marginBottom: 4 }}>
            {displayName ? `${displayName}さんのサマリー` : 'あなたのサマリー'}
          </h2>
          <p style={{ margin: 0, fontSize: 12, color: '#bfdbfe' }}>
            {new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{totalHours.toFixed(0)}<span style={{ fontSize: 12, fontWeight: 400 }}>h</span></div>
            <div style={{ fontSize: 10, color: '#93c5fd' }}>累計学習時間</div>
          </div>
          <div style={{ width: 1, background: '#3b82f6', opacity: 0.4 }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{Math.min((totalHours / 1000 * 100), 100).toFixed(0)}<span style={{ fontSize: 12, fontWeight: 400 }}>%</span></div>
            <div style={{ fontSize: 10, color: '#93c5fd' }}>成長達成率</div>
          </div>
        </div>
      </div>

      {/* ─── SNS共有（上部） ─── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <SNSShareButtons totalHours={totalHours} streak={streak} archetype={archetype} growthType={growthType} profile={profile} compact={true} />
      </div>

      {/* ─── 達成フィードミニウィジェット ─── */}
      {(() => {
        const [feedItems, setFeedItems] = React.useState([]);
        React.useEffect(() => {
          const q = query(collection(db, 'breakthroughs'), orderBy('createdAt', 'desc'), limit(3));
          const unsub = onSnapshot(q, snap => {
            setFeedItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          });
          return () => unsub();
        }, []);

        if (feedItems.length === 0) return null;

        const ARCHETYPE_COLORS = {
          LOGICAL_ARCHITECT:    { bg: '#ede9fe', text: '#5b21b6', emoji: '🧠' },
          EMPATHETIC_CONNECTOR: { bg: '#d1fae5', text: '#065f46', emoji: '🤝' },
          BOLD_PIONEER:         { bg: '#fee2e2', text: '#991b1b', emoji: '🚀' },
          FLEXIBLE_ARTISAN:     { bg: '#ffedd5', text: '#9a3412', emoji: '🔥' },
        };

        return (
          <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconActivity size={16} color="#6366f1"/>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>仲間の達成フィード</span>
                <span style={{ fontSize: 10, background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 99, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1.5s infinite' }}/>LIVE
                </span>
              </div>
              <a href="/dashboard?tab=achievements" style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, textDecoration: 'none' }}>
                全て見る →
              </a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {feedItems.map((item, i) => {
                const col = ARCHETYPE_COLORS[item.archetypeKey] || { bg: '#f3f4f6', text: '#374151', emoji: '⚡' };
                const is7 = item.dayNum === 7;
                const name = item.nickname || item.displayName || item.username || null;
                const timeAgo = (() => {
                  if (!item.createdAt?.seconds) return '';
                  const diff = (Date.now() / 1000) - item.createdAt.seconds;
                  if (diff < 3600) return `${Math.floor(diff/60)}分前`;
                  if (diff < 86400) return `${Math.floor(diff/3600)}時間前`;
                  return `${Math.floor(diff/86400)}日前`;
                })();
                return (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: is7 ? '#fffbeb' : '#f9fafb', borderRadius: 10, border: `1px solid ${is7 ? '#fde68a' : '#e5e7eb'}` }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: col.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      {col.emoji}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: '#111827', lineHeight: 1.5 }}>
                        <strong style={{ color: col.text }}>{name || `${col.emoji}タイプのユーザー`}</strong>{name ? 'さんが' : 'が'}
                        {is7
                          ? <><strong>🏆 7日間突破プランを完全制覇！</strong></>
                          : <>⚡ <strong>Step {item.dayNum}</strong> を突破！</>
                        }
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>{timeAgo}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ─── 突破プラン × 学習ログ 連携バナー ─── */}
      {(() => {
        const saved = (() => { try { return JSON.parse(localStorage.getItem('nobishiro_breakthrough_days') || '[]'); } catch { return []; } })();
        const pace = (() => { try { return parseFloat(localStorage.getItem('nobishiro_breakthrough_pace') || '1'); } catch { return 1; } })();
        const totalDays = Math.round(7 / pace);
        const completedCount = saved.length;
        const pct = Math.round((completedCount / 7) * 100);
        const paceLabel = pace === 0.5 ? '🐢 14日間' : pace === 0.75 ? '🚶 9日間' : pace === 1 ? '🏃 7日間' : pace === 1.5 ? '⚡ 5日間' : '🔥 4日間';
        const isStarted = completedCount > 0;
        const isDone = completedCount >= 7;
        return (
          <div style={{ marginBottom: 12 }}>
            <a href="/breakthrough" style={{ textDecoration: 'none', display: 'block' }}>
              <div style={{
                background: isDone
                  ? 'linear-gradient(135deg,#f59e0b,#ef4444)'
                  : isStarted
                    ? 'linear-gradient(135deg,#7c3aed,#6366f1)'
                    : 'linear-gradient(135deg,#f97316,#ef4444)',
                borderRadius: 14, padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: 14,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transition: 'transform 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform='translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform='none'}
              >
                <div style={{ fontSize: 28, flexShrink: 0 }}>
                  {isDone ? '🏆' : isStarted ? '🔥' : '🚀'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 700, marginBottom: 3 }}>
                    {isDone ? '突破プラン完了！' : isStarted ? `突破プラン進行中 · ${paceLabel}` : '🔥 突破プラン'}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: isStarted ? 6 : 0 }}>
                    {isDone
                      ? '全7ステップ完全突破！次のチャレンジへ'
                      : isStarted
                        ? `${completedCount}/7ステップ完了 · ${7 - completedCount}ステップ残り`
                        : '成長の壁を7ステップで突破する'}
                  </div>
                  {isStarted && !isDone && (
                    <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 99, height: 5, overflow: 'hidden' }}>
                      <div style={{ background: '#fff', height: '100%', width: `${pct}%`, borderRadius: 99, transition: 'width 0.4s' }} />
                    </div>
                  )}
                  {!isStarted && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>
                      学習時間 {totalHours.toFixed(0)}h の実績を活かして壁を突破しよう
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '5px 12px', flexShrink: 0 }}>
                  {isDone ? '再挑戦 →' : isStarted ? '続ける →' : '始める →'}
                </div>
              </div>
            </a>
          </div>
        );
      })()}

      {/* ─── ログ記録ボタン ─── */}
      <div style={{ marginBottom: 24, padding: '4px 0' }}>
        <button
          onClick={() => navigate('/learning-log')}
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            color: '#fff', border: 'none', borderRadius: 16,
            padding: '22px 24px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            fontSize: 16, fontWeight: 800,
            boxShadow: '0 6px 20px rgba(5,150,105,0.35)',
            transition: 'transform 0.15s, box-shadow 0.15s',
            letterSpacing: '-0.01em',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 10px 28px rgba(5,150,105,0.45)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 6px 20px rgba(5,150,105,0.35)'; }}
        >
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconMic size={22} color="#fff"/>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>学習ログを記録する</div>
            <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.85, marginTop: 2 }}>
              {totalHours > 0 ? `累計 ${totalHours.toFixed(1)}h · 音声入力対応` : '音声入力対応・スマホでもOK'}
            </div>
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 20, opacity: 0.8 }}>→</span>
        </button>
      </div>
      {/* ─── 学習時間統計 ─── */}
      <div className="nobishiro-stats-grid">
        <StatCard icon={Flame}      label="今週" value={weeklyHours}  sub="月〜日" color="#ef4444" highlight />
        <StatCard icon={Calendar}   label="今月" value={monthlyHours} sub={`${new Date().getMonth() + 1}月`} color="#f59e0b" />
        <StatCard icon={TrendingUp} label="累計" value={totalHours}   sub="合計" color="#3b6fd4" />
      </div>

      {/* ─── 成長の軌跡 ─── */}
      <div style={{ marginBottom: 20 }}>
        <ThousandHoursProgress totalHours={totalHours} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <ShareCard
            totalHours={totalHours}
            streak={streak}
            archetype={archetype}
            growthType={growthType}
            userName={user?.displayName || user?.email?.split('@')[0] || ''}
            neurodiversityProfile={profile?.neurodiversityProfile || null}
          />
        </div>
        <SNSShareButtons totalHours={totalHours} streak={streak} archetype={archetype} growthType={growthType} profile={profile} />
      </div>

      {/* ─── 週次目標編集モーダル ─── */}
      {editingGoal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#111827' }}>週次目標を設定</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>1週間の学習目標時間を設定してください</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 20 }}>
              <button onClick={() => setGoalInput(v => Math.max(1, Number(v) - 1))} style={{ width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #e5e7eb', background: '#f9fafb', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>−</button>
              <div style={{ textAlign: 'center' }}>
                <input type="number" min="1" max="168" value={goalInput} onChange={e => setGoalInput(Number(e.target.value))} style={{ width: 80, textAlign: 'center', fontSize: 36, fontWeight: 800, color: '#1e40af', border: 'none', outline: 'none' }} />
                <div style={{ fontSize: 14, color: '#6b7280', marginTop: -4 }}>時間 / 週</div>
              </div>
              <button onClick={() => setGoalInput(v => Math.min(168, Number(v) + 1))} style={{ width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #e5e7eb', background: '#f9fafb', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>+</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[3, 5, 7, 10, 14, 20].map(h => (
                <button key={h} onClick={() => setGoalInput(h)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1.5px solid ${goalInput === h ? '#1e40af' : '#e5e7eb'}`, background: goalInput === h ? '#eff6ff' : '#f9fafb', color: goalInput === h ? '#1e40af' : '#374151', fontSize: 12, fontWeight: goalInput === h ? 700 : 400, cursor: 'pointer' }}>{h}h</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEditingGoal(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={async () => { try { await updateProfile({ weeklyGoalHours: Number(goalInput) }); setEditingGoal(false); } catch(e) { alert('保存に失敗しました'); } }} style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #1e40af, #3b6fd4)', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 12px rgba(30,64,175,0.3)' }}>保存する</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 今週の目標 + 成長曲線 ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
        <WeeklyGoal weeklyHours={weeklyHours} profile={profile} onEditGoal={() => { setGoalInput(profile?.weeklyGoalHours || 7); setEditingGoal(true); }} />
        <GrowthChart chartData={chartData} />
      </div>

      {/* ─── スキル別内訳グラフ ─── */}
      <div style={{ background:'#fff', border:'1.5px solid #e5e7eb', borderRadius:14, padding:'16px 20px', marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
          <span style={{ fontSize:16 }}>📊</span>
          <span style={{ fontSize:14, fontWeight:700, color:'#374151' }}>スキル別学習内訳</span>
          <span style={{ fontSize:11, color:'#9ca3af', marginLeft:'auto' }}>累計 {(stats?.totalHours || 0).toFixed(1)}h</span>
        </div>
        <CategoryChart logs={stats.recentLogs || []} />
      </div>

      {/* ─── AIコーチング ─── */}
      <AICoaching stats={stats} profile={profile} streak={streak} />

      {/* ─── 成長タイプ詳細 ─── */}
      <div style={{ marginBottom: 20 }}>
        <NobishiroRadarChart profile={profile} />
        <GrowthTypeDetail profile={profile} />
      </div>

      {/* ─── 次のアクション + 最近のログ ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
        <NextActions profile={profile} stats={{ totalHours, weeklyHours }} />
        <RecentLogs logs={recentLogs} />
      </div>

      {/* ─── 学習カレンダー ─── */}
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>📅</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>学習カレンダー</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>過去35日</span>
          </div>
          {streak >= 1 && <span style={{ fontSize: 12, fontWeight: 700, color: '#f97316', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 20, padding: '2px 10px' }}>🔥 {streak}日連続</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 3 }}>
          {['月','火','水','木','金','土','日'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 10, color: '#9ca3af', paddingBottom: 4, fontWeight: 600 }}>{d}</div>
          ))}
          {(() => {
            const firstDay = calendarData[0]?.date;
            const offset = firstDay ? (firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1) : 0;
            const cells = [];
            for (let i = 0; i < offset; i++) cells.push(<div key={'pad'+i} />);
            calendarData.forEach((day, i) => {
              const h = day.hours;
              const isToday = day.date.toDateString() === new Date().toDateString();
              const bg = h === 0 ? '#f3f4f6' : h < 1 ? '#bfdbfe' : h < 2 ? '#60a5fa' : h < 3 ? '#3b82f6' : '#1d4ed8';
              cells.push(
                <div key={i} title={`${day.date.toLocaleDateString('ja-JP', {month:'numeric',day:'numeric'})} ${h.toFixed(1)}h`} style={{ height: 16, borderRadius: 3, background: bg, border: isToday ? '2px solid #f97316' : 'none', cursor: 'default', transition: 'transform 0.1s' }} onMouseEnter={e => e.currentTarget.style.transform='scale(1.15)'} onMouseLeave={e => e.currentTarget.style.transform='none'} />
              );
            });
            return cells;
          })()}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 10, color: '#9ca3af' }}>少</span>
          {['#f3f4f6','#bfdbfe','#60a5fa','#3b82f6','#1d4ed8'].map((c,i) => <div key={i} style={{ width: 12, height: 12, borderRadius: 3, background: c }} />)}
          <span style={{ fontSize: 10, color: '#9ca3af' }}>多</span>
        </div>
      </div>

      {/* ─── バッジ・実績 ─── */}
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 16 }}>🏅</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>獲得バッジ</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{earnedBadges.length} / {BADGES.length}</span>
        </div>
        {earnedBadges.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>
            まだバッジがありません。学習を記録してバッジを獲得しましょう！
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {earnedBadges.map(b => (
              <div key={b.id} title={b.desc} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg, #fefce8, #fef9c3)', border: '1.5px solid #fde68a', borderRadius: 10, padding: '6px 12px', cursor: 'default' }}>
                <span style={{ fontSize: 18 }}>{b.emoji}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>{b.name}</div>
                  <div style={{ fontSize: 10, color: '#b45309' }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {BADGES.filter(b => !earnedBadges.find(e => e.id === b.id)).length > 0 && (
          <details style={{ marginTop: 12 }}>
            <summary style={{ fontSize: 12, color: '#9ca3af', cursor: 'pointer', userSelect: 'none' }}>未獲得バッジを見る ({BADGES.length - earnedBadges.length}個)</summary>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {BADGES.filter(b => !earnedBadges.find(e => e.id === b.id)).map(b => (
                <div key={b.id} title={b.desc} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '6px 12px', opacity: 0.6 }}>
                  <span style={{ fontSize: 18, filter: 'grayscale(100%)' }}>{b.emoji}</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{b.name}</div>
                    <div style={{ fontSize: 10, color: '#6b7280' }}>{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* ─── プッシュ通知設定 ─── */}
      {typeof Notification !== 'undefined' && (
        <div style={{ background: '#fff', border: `1.5px solid ${notifEnabled ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20, transition: 'border-color 0.3s' }}>
          {/* ヘッダー行 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: notifEnabled ? 14 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: notifEnabled ? '#dcfce7' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔔</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>学習リマインダー</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  {notifEnabled ? `毎日 ${notifTime} に通知` : '毎日の学習をお知らせ'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {notifEnabled && (
                <button onClick={sendTestNotif} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', fontSize: 11, cursor: 'pointer', color: '#374151', fontWeight: 600 }}>
                  テスト
                </button>
              )}
              {/* トグルスイッチ */}
              <div
                onClick={() => notifEnabled ? setNotifEnabled(false) : requestNotifPermission()}
                style={{ width: 48, height: 26, borderRadius: 999, background: notifEnabled ? '#10b981' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.3s', flexShrink: 0 }}
              >
                <div style={{ position: 'absolute', top: 3, left: notifEnabled ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.3s' }} />
              </div>
            </div>
          </div>

          {/* 時刻設定（ON時のみ表示） */}
          {notifEnabled && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 8 }}>⏰ リマインダー時刻</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* プリセット時刻 */}
                {['07:00','12:00','18:00','20:00','22:00'].map(t => (
                  <button key={t} onClick={() => setNotifTime(t)} style={{ background: notifTime === t ? '#059669' : '#fff', color: notifTime === t ? '#fff' : '#374151', border: `1.5px solid ${notifTime === t ? '#059669' : '#d1d5db'}`, borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: notifTime === t ? 700 : 400, cursor: 'pointer' }}>
                    {t}
                  </button>
                ))}
                {/* カスタム時刻 */}
                <input type="time" value={notifTime} onChange={e => setNotifTime(e.target.value)} style={{ border: '1.5px solid #d1d5db', borderRadius: 8, padding: '4px 8px', fontSize: 12, color: '#374151', outline: 'none' }} />
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: '#6b7280' }}>
                💡 ブラウザが開いている時のみ通知されます。毎日の学習習慣作りにご活用ください。
              </p>
            </div>
          )}

          {/* 未許可の案内 */}
          {!notifEnabled && Notification.permission === 'denied' && (
            <div style={{ marginTop: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: '#dc2626' }}>
              ⚠️ 通知がブロックされています。ブラウザの設定から許可してください。
            </div>
          )}
        </div>
      )}

      {/* ─── 突破プランバナー ─── */}
      <div style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)', borderRadius: 14, padding: '16px 20px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 4, letterSpacing: '0.05em' }}>🔥 突破プラン</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', marginBottom: 4 }}>成長の壁を7ステップで突破する</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
            あなたのタイプに合わせた突破プランで、0.5x〜2xのペースから選べます
          </div>
        </div>
        <a href="/breakthrough" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: '#fff', color: '#ef4444',
          padding: '10px 18px', borderRadius: 10,
          fontSize: 13, fontWeight: 800, textDecoration: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0,
          transition: 'transform 0.15s',
        }}>
          🚀 突破プランへ
        </a>
      </div>

      {/* ─── 紹介バナー ─── */}
      <div style={{ background: 'linear-gradient(135deg,#1e1b4b,#312e81)', borderRadius: 14, padding: '16px 20px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 700, marginBottom: 4 }}>🎁 紹介特典あり</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 2 }}>仲間を誘って、一緒に成長しよう</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>1名紹介でStandardプラン1ヶ月無料</div>
        </div>
        <a href="/referral" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#312e81', padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 800, textDecoration: 'none', flexShrink: 0 }}>
          紹介URLを取得 →
        </a>
      </div>

      {/* ─── タブナビゲーション ─── */}
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '16px 20px' }}>
        <SectionHeader icon={Zap} title="詳細を確認する" color="#3b6fd4" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {[
            { tab: 'patterns', icon: '📈', label: '成長パターン診断', desc: '今の壁と突破口を見つける' },
            { tab: 'neuro',    icon: '🧠', label: '認知特性',         desc: '最適な学習スタイルを知る' },
            { tab: 'triz',     icon: '⚡', label: 'ブレイクスルー診断', desc: '課題の突破口を発見する' },
            { tab: 'team',     icon: '👥', label: 'チーム分析',       desc: 'メンバーの強みを活かす' },
          ].map(item => (
            <button
              key={item.tab}
              onClick={() => onTabChange && onTabChange(item.tab)}
              style={{
                background: '#f9fafb', border: '1.5px solid #e5e7eb',
                borderRadius: 10, padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b6fd4'; e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
            >
              <span style={{ fontSize: 20 }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.desc}</div>
              </div>
              <ArrowRight size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
